import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Check, Heart, BookOpen, MessageCircle, Play, X } from "lucide-react";
import { toast } from "sonner";
import SectionReveal from "@/components/SectionReveal";
import VideoModal from "@/components/VideoModal";
import useSeo from "@/hooks/useSeo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getRefCode } from "@/lib/referral";

const PRICE_AUD_LABEL = "$97 AUD";

const FALLBACK_MODULES = [
  {
    id: "she-hasnt-changed",
    number: "1",
    title: "She Hasn't Changed. She's Transitioning",
    tagline: "You're not imagining it. Something is happening — and understanding it changes everything.",
    summary:
      "What menopause actually is, what's happening in her brain and nervous system, and why your read of the situation is probably wrong in the most hopeful way possible.",
  },
  {
    id: "the-medical-context",
    number: "1b",
    title: "The Medical Context",
    tagline: "The HRT history that failed a generation of women — and how to be a useful advocate.",
    summary:
      "How to understand what she may be navigating in the healthcare system, the history of HRT, and how to support without taking over.",
  },
  {
    id: "what-she-needs",
    number: "2",
    title: "What She Needs — And What She Doesn't",
    tagline: "You want to fix it. That's usually the problem — and it comes from the best place.",
    summary:
      "Why the fix instinct backfires, what she might need from you instead, and five things that genuinely create connection.",
  },
  {
    id: "youre-in-this-too",
    number: "3",
    title: "You're In This Too",
    tagline: "Supporting someone well does not require disappearing yourself.",
    summary:
      "Your experience matters. Permission to acknowledge what you've been carrying — and why looking after yourself is how you show up well for her.",
  },
  {
    id: "the-conversations-that-matter",
    number: "4",
    title: "The Conversations That Matter Most",
    tagline:
      "She hasn't become someone who's difficult to talk to. She's become someone whose brain and nervous system are working harder than you can see.",
    summary:
      "What's happening on both sides of a difficult conversation and the specific tools that create safety rather than more distance.",
  },
  {
    id: "coming-through-it-together",
    number: "5",
    title: "Coming Through It Together",
    tagline: "This isn't the end of something. It's the beginning of something different.",
    summary:
      "What the other side of this looks like — and the research is genuinely hopeful. Includes Pip's Quick Reference Card.",
  },
];

export default function MensGuide() {
  useSeo({
    title: "A Man's Guide to Her Menopause · Pip Bennett",
    description:
      "What every man needs to know about his partner's menopause — and how to show up well. A six-module coaching program for the man who loves a woman going through menopause.",
    canonical: "https://pipbennett.com/a-mans-guide-to-her-menopause",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Course",
      name: "A Man's Guide to Her Menopause",
      description:
        "A six-module coaching program for the man who loves a woman going through menopause — and wants to understand what's actually happening, and what he can do that genuinely helps. Grounded in current menopause research and positive psychology coaching theory.",
      provider: {
        "@type": "Person",
        name: "Pip Bennett",
        url: "https://pipbennett.com",
        jobTitle:
          "Positive Psychology Coach, Clinical Hypnotherapist, Menopause Movement Certified Coach",
      },
      offers: {
        "@type": "Offer",
        price: "97",
        priceCurrency: "AUD",
        availability: "https://schema.org/InStock",
      },
      inLanguage: "en",
    },
  });

  const [modules, setModules] = useState(FALLBACK_MODULES);
  const [videoOpen, setVideoOpen] = useState(false);
  const [heroMsgOpen, setHeroMsgOpen] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { user } = useAuth();
  const alreadyOwns =
    user && user !== false && user.has_mens_guide_access;

  const proceedToCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "mens_guide",
        origin_url: window.location.origin,
        agreement_accepted: true,
        ref_code: getRefCode() || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Checkout couldn't start — could you try again?"
      );
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/mens-guide/preview")
      .then((r) => {
        if (Array.isArray(r.data?.modules) && r.data.modules.length) {
          setModules(r.data.modules);
        }
      })
      .catch(() => {
        /* keep fallback */
      });
  }, []);

  return (
    <div className="bg-cream" data-testid="page-mens-guide">
      {/* LAUNCH BANNER */}
      <div
        className="bg-sage text-cream"
        data-testid="mens-guide-banner"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 py-2.5 text-center text-xs md:text-sm tracking-wide">
          <span className="font-medium">Now available</span>
          <span className="mx-3 text-cream/40">·</span>
          <a
            href="#enrol"
            className="underline-offset-4 hover:underline text-cream/90"
          >
            Enrol today for {PRICE_AUD_LABEL} · lifetime access
          </a>
        </div>
      </div>

      {/* HERO */}
      <section className="relative pt-14 md:pt-24 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <SectionReveal className="lg:col-span-7">
            <div className="eyebrow text-sage mb-5" data-testid="mens-guide-hero-eyebrow">
              For the man who loves a woman in menopause
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              If menopause has changed the woman you love,
              <em className="italic text-sage"> it may have changed the relationship between you too.</em>
            </h1>
            <p className="mt-7 text-lg text-ink-soft leading-relaxed max-w-xl">
              You don&apos;t need to fix this. You need to understand it. This is the practical
              framework that helps you show up for her — and for the relationship — in a way that
              actually lands.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              {alreadyOwns ? (
                <Link
                  to="/dashboard/mens-guide"
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                  data-testid="mens-guide-hero-cta"
                >
                  Go to your dashboard
                  <ArrowRight size={16} strokeWidth={1.8} />
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={proceedToCheckout}
                  disabled={checkoutLoading}
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="mens-guide-hero-cta"
                >
                  {checkoutLoading ? "Opening checkout…" : `Enrol now · ${PRICE_AUD_LABEL}`}
                  {!checkoutLoading && <ArrowRight size={16} strokeWidth={1.8} />}
                </button>
              )}
              {!alreadyOwns && (
                <button
                  type="button"
                  onClick={() => setSampleOpen(true)}
                  className="inline-flex items-center justify-center gap-2 bg-cream-soft hover:bg-cream border border-sage/40 hover:border-sage text-sage hover:text-sage-dark rounded-full px-8 py-4 font-medium tracking-wide transition-all"
                  data-testid="mens-guide-hero-sample-cta"
                >
                  <Play size={14} strokeWidth={1.8} fill="currentColor" />
                  Watch Module 1 free
                </button>
              )}
            </div>
            <div className="mt-5 text-xs text-ink-soft/70 leading-relaxed">
              6 modules · self-paced · lifetime access · {PRICE_AUD_LABEL} — less than a dinner out
            </div>

            {/* Social proof — short, real quotes directly under the CTAs */}
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl"
              data-testid="mens-guide-hero-testimonials"
            >
              <figure className="bg-cream-soft border border-hairline rounded-2xl px-5 py-4">
                <blockquote className="font-serif-display text-base text-ink leading-snug italic">
                  This program is everything I wish I&apos;d had sooner. I spent months feeling
                  like I was doing everything wrong. Now I actually understand what&apos;s
                  happening for her — and that&apos;s changed everything about how I show up.
                </blockquote>
                <figcaption className="mt-3 text-xs text-ink-soft tracking-wide">
                  — David, 54
                </figcaption>
              </figure>
              <figure className="bg-cream-soft border border-hairline rounded-2xl px-5 py-4">
                <blockquote className="font-serif-display text-base text-ink leading-snug italic">
                  Finally. So good. I&apos;ve already been getting my mates onto this. Every man in
                  a relationship with a woman in menopause needs this — we just don&apos;t know what
                  we don&apos;t know.
                </blockquote>
                <figcaption className="mt-3 text-xs text-ink-soft tracking-wide">
                  — Jason, 46
                </figcaption>
              </figure>
            </div>
          </SectionReveal>

          <SectionReveal delay={150} className="lg:col-span-5">
            <div className="relative">
              <picture>
                <source srcSet="/images/mens-guide-hero.webp" type="image/webp" />
                <img
                  src="/images/mens-guide-hero.png"
                  alt="A group of men sitting together in warm conversation — His Guide to Her Menopause"
                  className="w-full h-auto rounded-3xl shadow-[0_30px_70px_-30px_rgba(74,93,79,0.45)]"
                  loading="eager"
                  fetchpriority="high"
                  data-testid="mens-guide-hero-image"
                />
              </picture>

              {/* HERO MESSAGE VIDEO — click-to-play, sits directly under the hero image */}
              <button
                type="button"
                onClick={() => setHeroMsgOpen(true)}
                aria-label="Play Pip's message"
                data-testid="mens-guide-hero-message-play"
                className="group mt-5 w-full bg-cream-soft border border-hairline rounded-2xl overflow-hidden shadow-[0_18px_45px_-20px_rgba(74,93,79,0.35)] flex items-center gap-4 p-4 hover:border-sage focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2 focus:ring-offset-cream-soft transition-all text-left"
              >
                <span className="relative flex-shrink-0 rounded-xl overflow-hidden" style={{ width: "92px", aspectRatio: "9/16" }}>
                  <picture>
                    <source srcSet="/images/pip-hero-message-poster.webp" type="image/webp" />
                    <img
                      src="/images/pip-hero-message-poster.jpg"
                      alt="Pip Bennett · a short message"
                      className="block w-full h-full object-cover"
                      loading="lazy"
                    />
                  </picture>
                  <span className="absolute inset-0 bg-ink/15 group-hover:bg-ink/30 transition-colors flex items-center justify-center">
                    <span className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-cream/95 text-sage shadow group-hover:scale-105 transition-transform">
                      <Play size={14} strokeWidth={1.8} fill="currentColor" />
                    </span>
                  </span>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block eyebrow text-sage mb-1">A message from Pip · 33 sec</span>
                  <span className="block font-serif-display text-base md:text-lg text-ink leading-snug">
                    "While this is her transition, <em className="italic text-sage">you're moving through it too</em>."
                  </span>
                </span>
              </button>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* MEET PIP — video intro · moved here so visitors see Pip's voice immediately under the hero photo */}
      <section className="pt-12 md:pt-16 pb-4 md:pb-6" data-testid="mens-guide-intro-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="bg-cream-soft border border-hairline rounded-3xl p-7 md:p-10 flex flex-col md:flex-row items-center gap-7 md:gap-10">
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="relative group flex-shrink-0 rounded-2xl overflow-hidden shadow-[0_20px_50px_-20px_rgba(74,93,79,0.45)] focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-4 focus:ring-offset-cream-soft transition"
                aria-label="Play Pip's introduction video"
                data-testid="mens-guide-intro-play"
                style={{ width: "200px" }}
              >
                <picture>
                  <source srcSet="/images/mens-guide-intro-poster.webp" type="image/webp" />
                  <img
                    src="/images/mens-guide-intro-poster.jpg"
                    alt="Pip Bennett — introduction to A Man's Guide to Her Menopause"
                    className="block w-full h-auto"
                    style={{ aspectRatio: "9/16", objectFit: "cover" }}
                    loading="lazy"
                  />
                </picture>
                <span className="absolute inset-0 bg-ink/15 group-hover:bg-ink/30 transition-colors flex items-center justify-center">
                  <span className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-cream/95 text-sage shadow-lg group-hover:scale-105 transition-transform">
                    <Play size={22} strokeWidth={1.8} fill="currentColor" />
                  </span>
                </span>
              </button>

              <div className="text-center md:text-left">
                <div className="eyebrow text-sage mb-3">Meet Pip</div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-tight">
                  A short introduction <em className="italic text-sage">from me to you</em>.
                </h3>
                <p className="mt-4 text-ink-soft leading-relaxed">
                  Before you enrol, take a few minutes to hear what this program is
                  — and what it isn't. There's no script and no sales talk — just me
                  speaking to you directly.
                </p>
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="mt-5 inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm tracking-wide group"
                  data-testid="mens-guide-intro-play-text"
                >
                  Watch the introduction
                  <ArrowRight size={14} strokeWidth={1.8} className="group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* OPENING NOTE — what's already happening */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">Something has shifted</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              And you can <em className="italic text-sage">feel it</em>.
            </h2>
            <div className="mt-7 space-y-5 text-ink-soft leading-relaxed">
              <p>
                She seems different. Conversations that used to feel easy now feel
                loaded. Intimacy has changed. You're trying harder than you ever have —
                and somehow making things worse.
              </p>
              <p>
                What's happening between you right now is almost certainly not what you
                think it is. This transition has historically been invisible to partners.
                The conversation about menopause has been happening without you in the
                room.
              </p>
              <p className="font-serif-display text-2xl text-ink italic">
                This program puts you in the room.
              </p>
              <p className="font-serif-display text-xl text-sage italic">
                And once you&apos;re in the room, you&apos;ll wonder how you were ever navigating
                this without it.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* THE NUMBERS */}
      <section className="py-20 md:py-28 bg-cream-soft border-y border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">The numbers worth knowing</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight max-w-2xl">
              These weren't relationships <em className="italic text-sage">without love</em>.
              They were relationships without understanding.
            </h2>
          </SectionReveal>

          <SectionReveal>
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              <div className="bg-cream border border-hairline rounded-2xl p-8">
                <div className="font-serif-display text-5xl text-sage tabular-nums">
                  73%
                </div>
                <p className="mt-4 text-ink leading-relaxed">
                  of women surveyed believed menopause or perimenopause contributed to
                  the breakdown of their marriage.
                </p>
              </div>
              <div className="bg-cream border border-hairline rounded-2xl p-8">
                <div className="font-serif-display text-5xl text-sage tabular-nums">
                  70%
                </div>
                <p className="mt-4 text-ink leading-relaxed">
                  said that better support and understanding may have positively impacted
                  the relationship.
                </p>
              </div>
            </div>
            <p className="mt-8 text-xs text-ink-soft/70 italic max-w-3xl">
              Source: 2022 survey by The Family Law Menopause Project and Newson Health
              Research &amp; Education.
            </p>
          </SectionReveal>

          <SectionReveal>
            <p className="mt-12 text-lg text-ink leading-relaxed max-w-3xl">
              That&apos;s a solvable problem. And the fact that you&apos;re here already puts you
              ahead of most. This program gives you the rest.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* THIS IS FOR YOU IF */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">This is for you if</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
              You love her and you're trying — but nothing seems to land the way you{" "}
              <em className="italic text-sage">intend it to</em>.
            </h2>
          </SectionReveal>

          <SectionReveal>
            <ul className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-5 max-w-3xl">
              {[
                "You've watched her change and haven't known whether to say something or say nothing — and both feel wrong.",
                "You love her and want to be there for her, but you genuinely don't know what being there actually looks like right now.",
                "You're worried — about her, about the distance between you, about whether things will find their way back.",
                "You're ready to stop guessing and start understanding — so you can be the partner this moment is asking you to be.",
              ].map((line, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 mt-1 inline-flex items-center justify-center rounded-full bg-sage text-cream">
                    <Check size={12} strokeWidth={2.2} />
                  </span>
                  <span className="text-ink-soft leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section
        id="what-inside"
        className="py-20 md:py-28 bg-cream-soft border-y border-hairline"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">What's inside</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              Six modules. <em className="italic text-sage">One framework</em>.
              <span className="block text-ink-soft/80 text-xl md:text-2xl mt-3 font-normal">
                Pip's STAY Through Menopause Method.
              </span>
            </h2>
          </SectionReveal>

          <SectionReveal>
            <ol className="mt-14 space-y-6">
              {modules.map((m) => (
                <li
                  key={m.id}
                  className="bg-cream border border-hairline rounded-2xl p-7 md:p-9 grid md:grid-cols-[auto_1fr] gap-6 md:gap-10"
                  data-testid={`mens-guide-module-${m.id}`}
                >
                  <div className="md:w-32">
                    <div className="eyebrow text-sage">Module {m.number}</div>
                  </div>
                  <div>
                    <h3 className="font-serif-display text-xl md:text-2xl text-ink leading-snug">
                      {m.title}
                    </h3>
                    <p className="mt-2 italic text-sage/80 text-sm leading-relaxed">
                      {m.tagline}
                    </p>
                    <p className="mt-4 text-ink-soft leading-relaxed">{m.summary}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-10 text-sm text-ink-soft/80 italic max-w-3xl">
              Each module includes embedded coaching questions, a weekly action and a
              face-to-camera teaching video from Pip. Grounded in the latest menopause
              research — including the work of Dr Mary Claire Haver, Dr Louise Newson and
              Dr Jen Gunter — and in positive psychology and transitions coaching theory.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* WATCH A FREE SAMPLE */}
      <section className="py-20 md:py-28" data-testid="mens-guide-sample-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="bg-sage/[0.04] border border-sage/20 rounded-3xl p-8 md:p-12 text-center">
              <div className="eyebrow text-sage mb-5">Want a feel for the work first?</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight max-w-2xl mx-auto">
                Watch the Module 1 sample <em className="italic text-sage">on me</em>.
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed max-w-xl mx-auto">
                A short teaching video from inside the program — covering what menopause
                actually is, what's happening in her brain and nervous system, and why
                your read of the situation is probably wrong in the most hopeful way
                possible. Free. No payment. Just your name and email so I know where to
                reach you.
              </p>
              <button
                type="button"
                onClick={() => setSampleOpen(true)}
                className="mt-9 inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-9 py-4 font-medium tracking-wide transition-all"
                data-testid="mens-guide-sample-cta"
              >
                <Play size={15} strokeWidth={1.8} fill="currentColor" />
                Watch the sample
              </button>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT YOU WALK AWAY WITH */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">What you walk away with</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
              Confusion replaced with <em className="italic text-sage">context</em>.
            </h2>
          </SectionReveal>

          <SectionReveal>
            <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-7 max-w-4xl">
              {[
                { icon: Heart, label: "A new way of understanding her — and the transition she's going through" },
                { icon: MessageCircle, label: "Practical communication tools you can use in real conversations" },
                { icon: BookOpen, label: "Pip's STAY Through Menopause Method — a four-principle framework" },
                { icon: Mail, label: "A Quick Reference Card for the moments when everything feels far away" },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex gap-4">
                    <span className="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full bg-sage/10 text-sage">
                      <Icon size={18} strokeWidth={1.6} />
                    </span>
                    <p className="text-ink leading-relaxed pt-1.5">{item.label}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-10 max-w-3xl font-serif-display italic text-lg md:text-xl text-sage leading-snug">
              Most men finish this program and say the same thing: I wish I&apos;d done this sooner.
            </p>
          </SectionReveal>
        </div>
      </section>
      <section className="py-20 md:py-28 bg-cream-soft border-y border-hairline">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <SectionReveal>
            <div className="eyebrow text-sage mb-4">About Pip</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              You do have influence here. Not over her hormones or her biology — but over
              whether <em className="italic text-sage">she feels alone in this</em>.
            </h2>
          </SectionReveal>
          <SectionReveal>
            <p className="mt-8 text-ink-soft leading-relaxed">
              I&apos;m Pip Bennett — I&apos;ve been working with women through menopause for years,
              and one thing kept showing up: good men, trying hard, with no map. Men who loved
              their partners deeply and had absolutely no framework for what was happening. I
              created this program for them. For you.
            </p>
            <p className="mt-5 text-ink-soft leading-relaxed">
              That difference — between trying alone and understanding alongside her —
              is enormous.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* OBJECTION HANDLERS — the 3 things men tell themselves before they walk away */}
      {!alreadyOwns && (
        <section
          className="py-20 md:py-28 bg-cream"
          data-testid="mens-guide-objections"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <SectionReveal>
              <div className="eyebrow text-sage mb-4 text-center">Before you decide</div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight text-center">
                The three things <em className="italic text-sage">most men think</em> before they sign up.
              </h2>
              <p className="mt-5 text-ink-soft text-center max-w-xl mx-auto leading-relaxed">
                If any of these sound like you, you're not alone — and we'd love to gently
                respond before you click away.
              </p>
            </SectionReveal>

            <div className="mt-12 space-y-4">
              <SectionReveal delay={60}>
                <details className="group bg-cream-soft border border-hairline rounded-2xl p-7 md:p-8 transition-all">
                  <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                    <span className="font-serif-display text-lg md:text-xl text-ink leading-snug">
                      I don&apos;t think she&apos;d want me doing this.
                    </span>
                    <span className="mt-1 text-sage text-xl leading-none group-open:rotate-45 transition-transform shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="mt-5 text-ink-soft leading-relaxed">
                    Most partners feel deeply seen when they realise their man has quietly taken
                    steps to understand. You don&apos;t have to tell her. You don&apos;t have to
                    make a thing of it. You just start to show up differently — and she feels it.
                    The relief on the other side of this is consistent.
                  </p>
                </details>
              </SectionReveal>

              <SectionReveal delay={120}>
                <details className="group bg-cream-soft border border-hairline rounded-2xl p-7 md:p-8 transition-all">
                  <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                    <span className="font-serif-display text-lg md:text-xl text-ink leading-snug">
                      What if it doesn&apos;t help?
                    </span>
                    <span className="mt-1 text-sage text-xl leading-none group-open:rotate-45 transition-transform shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="mt-5 text-ink-soft leading-relaxed">
                    Start with the free sample. Module 1 — yours, no payment, no
                    pressure. If it doesn't land for you, you've lost nothing. If it
                    does, you'll already know whether the rest is worth the {PRICE_AUD_LABEL}.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSampleOpen(true)}
                    className="mt-5 inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium text-sm tracking-wide"
                    data-testid="mens-guide-objection-sample-cta"
                  >
                    <Play size={13} strokeWidth={1.8} fill="currentColor" />
                    Watch the free sample
                  </button>
                </details>
              </SectionReveal>

              <SectionReveal delay={180}>
                <details className="group bg-cream-soft border border-hairline rounded-2xl p-7 md:p-8 transition-all">
                  <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                    <span className="font-serif-display text-lg md:text-xl text-ink leading-snug">
                      I&apos;m not really a course-taker.
                    </span>
                    <span className="mt-1 text-sage text-xl leading-none group-open:rotate-45 transition-transform shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="mt-5 text-ink-soft leading-relaxed">
                    This isn&apos;t really a course. It&apos;s six short modules you watch
                    whenever you want — at the desk, on the couch, in the car. No deadlines, no
                    homework, no group calls. Just clear, honest content built for men who&apos;d
                    rather get on with it. And lifetime access means it&apos;s there the day you
                    actually need it.
                  </p>
                </details>
              </SectionReveal>
            </div>
          </div>
        </section>
      )}

      {/* ENROL SECTION */}
      <EnrolSection
        alreadyOwns={alreadyOwns}
        loading={checkoutLoading}
        onCheckout={proceedToCheckout}
      />

      {/* DISCLAIMER */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs text-ink-soft/70 italic leading-relaxed">
            This is a coaching and education program — not therapy, couples counselling
            or medical advice. Everything inside is ICF-aligned and grounded in current
            menopause research. If either of you needs deeper professional support,
            please seek it. This program works beautifully alongside that.
          </p>
        </div>
      </section>

      {videoOpen && (
        <VideoModal
          src="/videos/mens-guide-intro.mp4"
          poster="/images/mens-guide-intro-poster.jpg"
          title="A Man's Guide to Her Menopause · Introduction"
          onClose={() => setVideoOpen(false)}
        />
      )}

      {heroMsgOpen && (
        <VideoModal
          src="/videos/pip-hero-message.mp4"
          poster="/images/pip-hero-message-poster.jpg"
          title="A message from Pip"
          onClose={() => setHeroMsgOpen(false)}
        />
      )}

      {sampleOpen && (
        <SampleGate onClose={() => setSampleOpen(false)} />
      )}

      {!alreadyOwns && (
        <StickyEnrolButton
          loading={checkoutLoading}
          onClick={proceedToCheckout}
        />
      )}
    </div>
  );
}

function StickyEnrolButton({ loading, onClick }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Show once visitor has scrolled past the hero (roughly 600px).
      setVisible(window.scrollY > 600);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      className={`fixed z-40 right-4 md:right-6 bottom-4 md:bottom-6 transition-all duration-300 ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-6 opacity-0 pointer-events-none"
      }`}
      data-testid="mens-guide-sticky-enrol"
    >
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-3.5 font-medium tracking-wide shadow-[0_18px_40px_-12px_rgba(74,93,79,0.55)] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        aria-label={`Enrol now for ${PRICE_AUD_LABEL}`}
      >
        {loading ? "Opening checkout…" : `Enrol now · ${PRICE_AUD_LABEL}`}
        {!loading && <ArrowRight size={15} strokeWidth={1.8} />}
      </button>
    </div>
  );
}

function EnrolSection({ alreadyOwns, loading, onCheckout }) {
  return (
    <section id="enrol" className="py-20 md:py-28 bg-sage text-cream">
      <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
        <SectionReveal>
          <div className="eyebrow text-gold mb-5">Ready to start</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-cream leading-tight">
            Enrol in <em className="italic text-gold">A Man's Guide to Her Menopause</em>.
          </h2>
          <p className="mt-6 text-cream/80 leading-relaxed">
            Six modules. Lifetime access. Watch at your own pace, return whenever you
            need to. One payment of <span className="text-gold">{PRICE_AUD_LABEL}</span>.
          </p>
          <p
            className="mt-3 text-cream/70 text-sm leading-relaxed"
            data-testid="mens-guide-price-clarity"
          >
            {PRICE_AUD_LABEL}, one-time. Lifetime access. No subscription. No upsells.
          </p>
        </SectionReveal>

        <SectionReveal>
          <div className="mt-12">
            {alreadyOwns ? (
              <div
                className="bg-cream/10 border border-cream/20 rounded-2xl p-8 md:p-10"
                data-testid="mens-guide-already-own"
              >
                <div className="font-serif-display text-2xl text-cream leading-snug">
                  You already own this program.
                </div>
                <p className="mt-3 text-cream/80 leading-relaxed">
                  Head straight to your dashboard to pick up where you left off.
                </p>
                <Link
                  to="/dashboard/mens-guide"
                  className="mt-7 inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-3.5 font-medium tracking-wide transition-all"
                  data-testid="mens-guide-enrol-dashboard-link"
                >
                  Go to your dashboard
                  <ArrowRight size={15} strokeWidth={1.8} />
                </Link>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCheckout}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-10 py-4 font-medium tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="mens-guide-enrol-cta"
                >
                  {loading ? "Opening checkout…" : `Enrol now · ${PRICE_AUD_LABEL}`}
                  {!loading && <ArrowRight size={15} strokeWidth={1.8} />}
                </button>
                <p className="mt-6 text-xs text-cream/60 leading-relaxed max-w-md mx-auto">
                  Secure Stripe checkout. Instant access. Includes all six modules,
                  embedded reflection prompts, and Pip's STAY framework.
                </p>
              </>
            )}
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}

function SampleGate({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/mens-guide/sample", { name, email });
      setUnlocked(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't load the sample just now — could you try again?"
      );
    } finally {
      setLoading(false);
    }
  };

  if (unlocked) {
    return (
      <VideoModal
        src="/videos/mens-guide-module-1.mp4"
        poster="/images/mens-guide-module-1-poster.jpg"
        title="A Man's Guide to Her Menopause · Module 1 Sample"
        onClose={onClose}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      data-testid="mens-guide-sample-gate"
    >
      <div className="bg-cream rounded-3xl max-w-md w-full p-8 md:p-10 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-cream-soft text-ink-soft hover:text-ink transition-colors"
          aria-label="Close"
          data-testid="mens-guide-sample-gate-close"
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        <div className="eyebrow text-sage mb-3">Module 1 · Free sample</div>
        <h3 className="font-serif-display text-2xl md:text-3xl text-ink leading-snug">
          Where shall I send <em className="italic text-sage">your unlock</em>?
        </h3>
        <p className="mt-4 text-sm text-ink-soft leading-relaxed">
          The video opens immediately. I'll also keep your details so I can send you
          the occasional note about the work — no other emails unless you want them.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] text-ink-soft/70 mb-2">
              Your first name
            </span>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. James"
              className="w-full bg-cream-soft border border-hairline rounded-xl px-4 py-3 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition"
              data-testid="mens-guide-sample-name"
              autoFocus
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.18em] text-ink-soft/70 mb-2">
              Email
            </span>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yours.com"
              className="w-full bg-cream-soft border border-hairline rounded-xl px-4 py-3 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition"
              data-testid="mens-guide-sample-email"
            />
          </label>

          {error && (
            <p className="text-sm text-red-600/80" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-6 py-3.5 font-medium tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="mens-guide-sample-submit"
          >
            {loading ? "Unlocking…" : "Unlock the sample"}
            {!loading && <ArrowRight size={14} strokeWidth={1.8} />}
          </button>
        </form>
      </div>
    </div>
  );
}
